import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../ui/table/index";
import Badge from "../ui/badge/Badge";
import { useNavigate } from "react-router";
// import { OrderStatus } from "./Order";
import Spinner from "../ui/Spinner/Spinner";
import { FiPackage } from "react-icons/fi";
import { format } from "date-fns";
import { useOrdersStore } from "../../stores/useOrderStore";
import { getOrderStatusBadgeColor } from "../../utils/helpers";

export default function RecentOrders() {
  const navigate = useNavigate();
  const { recentOrders, hasInitialLoad, loading } = useOrdersStore();

  // ---- Navigate to all orders
  const gotoAllOrders = () => {
    navigate("/exchanges");
  };

  // ---- Formatted Date
  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd/MM/yyyy");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner className="h-10 w-10 text-blue-500" />
      </div>
    );
  }

  // Initial empty state (no orders at all)
  if (hasInitialLoad && recentOrders.length === 0) { // ✅ Use recentOrders instead of orders
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <FiPackage className="w-12 h-12 mb-4" />
        <p className="text-lg">No recent exchange requests</p>
        <p className="text-sm">Your recent exchange requests will appear here</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden w-full rounded-2xl border border-gray-200 bg-white px-4 pb-3 pt-4 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6">
      <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
          Recent Exchange Request
        </h3>
        <div className="flex items-center gap-4">
          <button
            onClick={gotoAllOrders}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-theme-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-white/[0.03] dark:hover:text-gray-200"
          >
            See all
          </button>
        </div>
      </div>

      <div className="max-w-full overflow-x-auto">
        {/* Desktop Table */}
        <div className="hidden sm:block">
          <Table>
            <TableHeader className="border-gray-100 dark:border-gray-800 border-y">
              <TableRow>
                <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                  Currency Pair
                </TableCell>
                <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                  Outgoing
                </TableCell>
                <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                  Incoming
                </TableCell>
                <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                  Date
                </TableCell>
                <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                  Status
                </TableCell>
              </TableRow>
            </TableHeader>

            <TableBody className="divide-y divide-gray-100 dark:divide-gray-800">
              {recentOrders.map((order) => ( // ✅ Use recentOrders directly
                <TableRow key={order._id}>
                  <TableCell className="py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center -space-x-2">
                        <div className="h-8 w-8 rounded-full overflow-hidden border-2 border-white dark:border-gray-800">
                          <img
                            src={order.fromCurrency.flag}
                            className="h-full w-full object-cover"
                            alt={order.fromCurrency.name}
                          />
                        </div>
                        <div className="h-8 w-8 rounded-full overflow-hidden border-2 border-white dark:border-gray-800">
                          <img
                            src={order.toCurrency.flag}
                            className="h-full w-full object-cover"
                            alt={order.toCurrency.name}
                          />
                        </div>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 text-theme-sm dark:text-white/90">
                          {order.fromCurrency.abbr}/{order.toCurrency.abbr}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                    {order.fromCurrency.symbol}
                    {order.amount.toLocaleString()}
                  </TableCell>
                  <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                    {order.toCurrency.symbol}
                    {order.value.toLocaleString()}
                  </TableCell>
                  <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                    {formatDate(order.createdAt)}
                  </TableCell>
                  <TableCell className="py-3">
                    <Badge size="sm" color={getOrderStatusBadgeColor(order.status)}>
                      {order.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Mobile Table */}
        <div className="sm:hidden">
          <Table>
            <TableHeader className="border-gray-100 dark:border-gray-800 border-y">
              <TableRow>
                <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                  Currency
                </TableCell>
                <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                  Outgoing
                </TableCell>
                <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                  Incoming
                </TableCell>
                <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                  Status
                </TableCell>
              </TableRow>
            </TableHeader>

            <TableBody className="divide-y divide-gray-100 dark:divide-gray-800">
              {recentOrders.map((order) => ( // ✅ Use recentOrders directly
                <TableRow key={order._id}>
                  <TableCell className="py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center -space-x-2">
                        <div className="h-8 w-8 rounded-full overflow-hidden border-2 border-white dark:border-gray-800">
                          <img
                            src={order.fromCurrency.flag}
                            className="h-full w-full object-cover"
                            alt={order.fromCurrency.name}
                          />
                        </div>
                        <div className="h-8 w-8 rounded-full overflow-hidden border-2 border-white dark:border-gray-800">
                          <img
                            src={order.toCurrency.flag}
                            className="h-full w-full object-cover"
                            alt={order.toCurrency.name}
                          />
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                    {order.fromCurrency.symbol}
                    {order.amount.toLocaleString()}
                  </TableCell>
                  <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                    {order.toCurrency.symbol}
                    {order.value.toLocaleString()}
                  </TableCell>
                  <TableCell className="py-3">
                    <Badge size="sm" color={getOrderStatusBadgeColor(order.status)}>
                      {order.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}